import { useCallback, useMemo, useRef } from 'react';
import useQuery, { QueryKey } from 'hooks/useQuery';
import { CancelToken } from 'axios';
import { message } from 'antd';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import {
  OperationSummaryError,
  OperationSummaryParams,
  OperationSummaryResponse
} from '../Components/types';
import { RECORD_SOURCE_MESSAGE_KEY } from '../Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';

interface UseOperationSummaryReturn<T> {
  loading: boolean;
  items: T[];
  totalElements: number;
  totalPages: number;
  pageSize: number;
  currentPage: number;
  numberOfElements: number;
  loadItems: () => Promise<void>;
  errors: OperationSummaryError | null;
}

const useOperationSummary = <T,>(
  queryKey: string,
  fetchFn: (
    params?: OperationSummaryParams,
    cancelToken?: CancelToken
  ) => Promise<OperationSummaryResponse<T>>,
  params?: OperationSummaryParams,
  cacheKeyPrefix?: string
): UseOperationSummaryReturn<T> => {
  const firstRenderRef = useRef(true);
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();

  const queryFn = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_queryKey: QueryKey, cancelToken?: CancelToken) => {
      // Destroy message if not first render and params include id
      if (!firstRenderRef.current && params?.id) {
        message.destroy(RECORD_SOURCE_MESSAGE_KEY);
      }

      const response = await fetchFn(params, cancelToken);

      firstRenderRef.current = false;
      return response;
    },
    [fetchFn, params]
  );

  const { data, isLoading, refetch } = useQuery<OperationSummaryResponse<T>>(
    queryKey,
    queryFn,
    {
      cacheKeyPrefix: cacheKeyPrefix || queryKey,
      cacheTTL: 0, // Disable caching for operation summary
      autoLoadingEnabled: selectedCropSeasonIds.length > 0
    }
  );

  const items = useMemo(() => data?.results || [], [data]);
  const totalElements = useMemo(() => data?.totalElements || 0, [data]);
  const totalPages = useMemo(() => data?.totalPages || 0, [data]);
  const pageSize = useMemo(() => data?.size || 0, [data]);
  const currentPage = useMemo(() => data?.currentPage || 0, [data]);
  const numberOfElements = useMemo(() => data?.numberOfElements || 0, [data]);
  const errors = useMemo(() => data?.errors || null, [data]);

  const loadItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    loading: isLoading,
    items,
    totalElements,
    totalPages,
    pageSize,
    currentPage,
    numberOfElements,
    loadItems,
    errors
  };
};

export default useOperationSummary;
